import cn from 'classnames';
import useLocalStudioRecentlyViewed from 'hooks/useLocalStudioRecentlyViewed';
import useRoutes from 'hooks/useRoutes';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RouteType } from 'routes';
import { Store } from 'store';
import getCategoryById from 'store/app/selector/getCategoryById';
import { setStudioSearchRefetchFlagAction } from 'store/studioSearch/slice';
import styles from './StudioSelectionSearchBar.module.scss';

type OwnProps = {
  className?: string;
  initialQuery?: string;
  onSearch?: (query: string) => void;
  onShowRecent?: () => void;
  isShownRecentlyViewed?: boolean;
  categoryId?: number | null;
};

const StudioSelectionSearchBar: React.FC<OwnProps> = ({
  className,
  initialQuery,
  onSearch,
  onShowRecent,
  isShownRecentlyViewed = true,
  categoryId,
}) => {
  const searchBarRef = useRef<HTMLElement>();
  const history = useHistory();
  const dispatch = useDispatch();
  const { clearRecentlyViewed } = useLocalStudioRecentlyViewed();
  const routes = useRoutes();
  const parentCategoryId = useSelector(
    (store: Store) => getCategoryById(store, Number(categoryId))?.parentCategoryId || categoryId,
  );
  const pathname = routes.reduce(
    (path, item) =>
      item.type === RouteType.STUDIO_SEARCH_PAGE &&
      !item.withLocalePrefix &&
      item.parameters?.some(({ name, value }) => name === 'categoryId' && value === parentCategoryId)
        ? item.path
        : path,
    '/studio-selection/getty/search',
  );

  const doSearch = (ev: any) => {
    if (onSearch) {
      onSearch(ev.detail.query);
    } else {
      const urlSearchParams = new URLSearchParams({
        query: ev.detail.query,
      });
      dispatch(setStudioSearchRefetchFlagAction(true));
      history.push({
        pathname,
        search: urlSearchParams.toString(),
      });
    }
  };

  const doShowRecent = () => {
    if (onShowRecent) {
      onShowRecent();
    } else {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set('recent', '');
      history.push({
        pathname,
        search: urlSearchParams.toString(),
      });
    }
  };

  useEffect(() => {
    searchBarRef.current?.addEventListener('search', doSearch);
    return () => {
      searchBarRef.current?.removeEventListener('search', doSearch);
    };
  });

  useEffect(() => {
    searchBarRef.current?.addEventListener('showRecent', doShowRecent);
    return () => {
      searchBarRef.current?.removeEventListener('showRecent', doShowRecent);
    };
  });

  useEffect(() => {
    searchBarRef.current?.addEventListener('clearRecent', clearRecentlyViewed);
    return () => {
      searchBarRef.current?.removeEventListener('clearRecent', clearRecentlyViewed);
    };
  });

  return (
    <div className={cn(styles.container, className)}>
      <search-bar
        ref={searchBarRef}
        initial-query={initialQuery}
        show-recently-viewed={isShownRecentlyViewed ? 'true' : 'false'}
      />
    </div>
  );
};

export default StudioSelectionSearchBar;
